import React from "react";
import PropTypes from "prop-types";
import { Button, Dialog, Grow, IconButton } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import PowerSettingsNewRoundedIcon from "@mui/icons-material/PowerSettingsNewRounded";
import "./modal.scss";

/* Declared at module scope: inline, the Dialog would remount the transition on
   every render and restart the animation mid-flight. */
const SmoothGrow = React.forwardRef(function SmoothGrow(props, ref) {
  return <Grow ref={ref} timeout={{ enter: 320, exit: 200 }} {...props} />;
});

const ActiveandInactiveModal = ({
  open,
  toggleModal,
  handleActive,
  projectData,
  switchState = {},
}) => {
  /* The caller already tracks the live toggle state, so the dialog can name the
     actual transition instead of asking an open-ended question. Falls back to
     the record's own flag if the map has no entry yet. */
  const isActive =
    switchState?.[projectData?._id] ?? projectData?.active === "true";

  const currentLabel = isActive ? "Active" : "Inactive";
  const nextLabel = isActive ? "Inactive" : "Active";

  return (
    <Dialog
      open={open}
      onClose={toggleModal}
      aria-labelledby="project-status-dialog-title"
      maxWidth="xs"
      fullWidth
      TransitionComponent={SmoothGrow}
      PaperProps={{ className: "pj-modal" }}
      slotProps={{ backdrop: { className: "pj-modal-backdrop" } }}
    >
      <div className="pj-modal-body">
        <IconButton
          className="pj-modal-close"
          size="small"
          onClick={toggleModal}
          aria-label="Close"
        >
          <CloseRoundedIcon fontSize="small" />
        </IconButton>

        <span className="pj-modal-badge">
          <PowerSettingsNewRoundedIcon />
        </span>

        <h3 className="pj-modal-title" id="project-status-dialog-title">
          Change project status
        </h3>
        <p className="pj-modal-sub">
          This changes whether the project shows as active for your team. You
          can switch it back at any time.
        </p>

        <div className="pj-modal-target">
          <span className="pj-modal-target-label">Project</span>
          <span className="pj-modal-target-name">{projectData?.title}</span>
        </div>

        <div className="pj-modal-transition">
          <span className={`pj-pill ${isActive ? "is-active" : "is-inactive"}`}>
            {currentLabel}
          </span>
          <ArrowForwardRoundedIcon className="pj-modal-arrow" />
          <span
            className={`pj-pill is-next ${isActive ? "is-inactive" : "is-active"}`}
          >
            {nextLabel}
          </span>
        </div>
      </div>

      <div className="pj-modal-actions">
        <Button className="pj-btn pj-btn--quiet" type="button" onClick={toggleModal}>
          Cancel
        </Button>
        <Button
          className="pj-btn pj-btn--primary"
          type="submit"
          onClick={() => handleActive(projectData._id)}
        >
          {isActive ? "Yes, deactivate" : "Yes, activate"}
        </Button>
      </div>
    </Dialog>
  );
};

ActiveandInactiveModal.propTypes = {
  open: PropTypes.bool.isRequired,
  toggleModal: PropTypes.func.isRequired,
  handleActive: PropTypes.func.isRequired,
  projectData: PropTypes.object.isRequired,
  switchState: PropTypes.object,
};

export default ActiveandInactiveModal;
